import React, { useState } from 'react';
import { useAuth, type ServerConfig } from '../contexts/AuthContext';
import { DISPLAY_CONFIG } from '../config/display';
import { PRIVACY_URL, SUPPORT_URL, TERMS_URL } from '../config/legal';
import { formatMoney } from '../utils/money';
import whiteoutLogo from '../assets/whiteout-logo.png';
import { ProviderLogo } from './ProviderLogos';

const PROVIDER_LABEL: Record<string, string> = { apple: 'Continue with Apple', google: 'Continue with Google' };
const PROVIDER_NAME: Record<string, string> = { apple: 'Apple', google: 'Google' };

/** A server-supplied page address, if it is one; anything else falls back to the built-in page. */
const pageUrl = (value: string | null | undefined, fallback: string) =>
  (typeof value === 'string' && /^https?:\/\//i.test(value) ? value : fallback);

/**
 * Where the policies live. The server may point elsewhere through /api/config;
 * otherwise the public pages on the site are used, so the links are never
 * missing, including in the native app before or without that config.
 */
export function legalUrls(serverConfig?: Pick<ServerConfig, 'privacyUrl' | 'termsUrl'> | null) {
  return {
    privacy: pageUrl(serverConfig?.privacyUrl, PRIVACY_URL),
    terms: pageUrl(serverConfig?.termsUrl, TERMS_URL),
    support: SUPPORT_URL,
  };
}

/** Privacy policy · Terms · Support. Opened outside the app (Safari on iOS). */
export const LegalLinks: React.FC<{ serverConfig?: ServerConfig | null; className?: string }> = ({ serverConfig, className = 'auth-legal' }) => {
  const urls = legalUrls(serverConfig);
  return (
    <p className={className}>
      <a href={urls.privacy} target="_blank" rel="noopener noreferrer">Privacy policy</a>
      <span aria-hidden="true"> · </span>
      <a href={urls.terms} target="_blank" rel="noopener noreferrer">Terms</a>
      <span aria-hidden="true"> · </span>
      <a href={urls.support} target="_blank" rel="noopener noreferrer">Support</a>
    </p>
  );
};

// Usernames and addresses are not prose: no capitals, corrections or squiggles.
const PLAIN_TEXT_ENTRY = { autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false } as const;
// Sign-in takes a username or an email address, and an address can be as long
// as 254 characters; only a new username is limited to 3–20.
const SIGN_IN_NAME = { maxLength: 254 } as const;
const NEW_USERNAME = { minLength: 3, maxLength: 20 } as const;

const Auth: React.FC = () => {
  const { login, register, loginWithProvider, socialProviders, serverConfig } = useAuth();
  const [pendingProvider, setPendingProvider] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    if (!isLogin && password !== confirmPassword) return setError('Passwords do not match');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    setIsLoading(true);
    try {
      const result = isLogin ? await login(username, password) : await register(username, email, password);
      // Registration always ends in a 202 and no session (new and existing
      // addresses get the same answer), so a successful sign-up lands here: the
      // form switches to sign-in, keeps the username, and shows the server's
      // "account created, please sign in" message as a notice, not an error.
      if (!result.success && (result as { pending?: boolean }).pending) {
        setNotice(result.error || 'Please sign in.');
        setIsLogin(true);
      } else if (!result.success) {
        setError(result.error || (isLogin ? 'Login failed' : 'Registration failed'));
      }
    } catch {
      setError('Unable to connect. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(value => !value);
    setError('');
    setNotice('');
  };

  const handleProvider = async (provider: string) => {
    setError(''); setNotice(''); setPendingProvider(provider);
    try {
      const result = await loginWithProvider(provider as 'apple' | 'google');
      if (!result.success && result.error) setError(result.error);
    } finally {
      setPendingProvider(null);
    }
  };

  return (
    <main className="auth-screen">
      <section className="auth-visual">
        <div className="auth-brand">
          <img src={whiteoutLogo} alt="" aria-hidden="true" />
          <span>{DISPLAY_CONFIG.brandName}</span>
        </div>
        <div className="auth-hero">
          <div className="auth-kicker">Alpine crash · Multiplayer</div>
          <h1>Hold your line. Know when to leave.</h1>
          <p>A fast, social run down an unpredictable mountain. Lock in your wager, watch the multiplier climb and cash out before conditions turn.</p>
        </div>
        <div className="auth-meta">Provably fair · Free play credits</div>
      </section>

      <section className="auth-form-side">
        <div className="auth-card panel">
          <h2>{isLogin ? 'Enter the run' : 'Join the mountain'}</h2>
          <p className="auth-subtitle">{isLogin ? `Sign in to continue to ${DISPLAY_CONFIG.brandName}.` : 'Create a player profile.'}</p>
          {socialProviders.length > 0 && (
            <div className="auth-social">
              {socialProviders.map(provider => (
                <button
                  key={provider}
                  type="button"
                  className={`auth-social-button auth-social-${provider}`}
                  onClick={() => handleProvider(provider)}
                  disabled={pendingProvider !== null}
                >
                  <ProviderLogo provider={provider} className="auth-social-logo" />
                  <span>{pendingProvider === provider ? 'Opening…' : PROVIDER_LABEL[provider]}</span>
                </button>
              ))}
              {/* Either button can create an account, from the sign-in screen as
                  well as from sign-up, so the age and Terms confirmation sits
                  with the buttons themselves. */}
              <p className="auth-legal auth-consent auth-social-consent">
                By continuing with {socialProviders.map(provider => PROVIDER_NAME[provider] ?? provider).join(' or ')} you confirm you are 18+ and accept the{' '}
                <a href={legalUrls(serverConfig).terms} target="_blank" rel="noopener noreferrer">Terms</a>.
              </p>
              <div className="auth-divider"><span>or</span></div>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="auth-field">
              <label htmlFor="username">{isLogin ? 'Username or email' : 'Username'}</label>
              <input id="username" className="field" value={username} onChange={e => setUsername(e.target.value)} placeholder={isLogin ? 'Your player name or email' : 'Your player name'} required {...(isLogin ? SIGN_IN_NAME : NEW_USERNAME)} autoComplete="username" {...PLAIN_TEXT_ENTRY} />
            </div>
            {!isLogin && <div className="auth-field"><label htmlFor="email">Email</label><input id="email" className="field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" required autoComplete="email" {...PLAIN_TEXT_ENTRY} /></div>}
            <div className="auth-field">
              <label htmlFor="password">Password</label>
              <input id="password" className="field" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 8 characters" required minLength={8} autoComplete={isLogin ? 'current-password' : 'new-password'} />
            </div>
            {!isLogin && <div className="auth-field"><label htmlFor="confirm-password">Confirm password</label><input id="confirm-password" className="field" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat your password" required minLength={8} autoComplete="new-password" /></div>}
            {notice && <div className="auth-notice" role="status">{notice}</div>}
            {error && <div className="auth-error" role="alert">{error}</div>}
            <button type="submit" disabled={isLoading} className="btn-primary btn-bet">{isLoading ? 'Please wait…' : isLogin ? `Enter ${DISPLAY_CONFIG.brandName}` : 'Create account'}</button>
            {!isLogin && (
              <p className="auth-legal auth-consent">
                By creating an account you confirm you are 18+ and accept the{' '}
                <a href={legalUrls(serverConfig).terms} target="_blank" rel="noopener noreferrer">Terms</a>.
              </p>
            )}
          </form>
          <button onClick={toggleMode} className="auth-switch">{isLogin ? 'New here? Create an account' : 'Already registered? Sign in'}</button>
          <p className="auth-demo"><span>{DISPLAY_CONFIG.freePlayLabel}</span><br />Starting balance {formatMoney(1000)}</p>
          <LegalLinks serverConfig={serverConfig} />
        </div>
      </section>
    </main>
  );
};

export default Auth;
